import type { LegendComponentType, LegendReferenceType } from '@ardoq/graph';
import type {
  RelationshipsLink,
  RelationshipsNode,
  VisualStatesData,
} from '../types';
import { type InteractiveRelationshipsViewLegendState } from 'tabview/relationshipDiagrams/interactiveRelationshipsViewLegendState$';
import {
  getMatchingFilterIdsFilter,
  getReferenceTypesFilter,
} from '../layout/util';
import { includeAllDescendants } from '../util';

const getSelectedNodes = (
  allNodes: RelationshipsNode[],
  selectedComponentTypeIds: string[][] | null,
  selectedConditionalFormatting: string[] | null
) => {
  const componentTypesSet = new Set(selectedComponentTypeIds?.flat() ?? []);
  const nodeMatchesComponentType = selectedComponentTypeIds?.length
    ? (node: RelationshipsNode) =>
        Boolean(node.typeModelId && componentTypesSet.has(node.typeModelId))
    : () => false;
  const nodeMatchesSelectedFormatting = getMatchingFilterIdsFilter(
    new Set(selectedConditionalFormatting)
  );
  return allNodes.filter(
    node =>
      nodeMatchesComponentType(node) || nodeMatchesSelectedFormatting(node)
  );
};

const getSelectedLinks = (
  links: RelationshipsLink[],
  selectedReferenceTypeIds: string[][] | null,
  selectedConditionalFormatting: string[] | null
) => {
  const linkMatchesReferenceTypes = selectedReferenceTypeIds?.length
    ? getReferenceTypesFilter(selectedReferenceTypeIds)
    : () => false;
  const linkMatchesConditionalFormatting = getMatchingFilterIdsFilter(
    new Set(selectedConditionalFormatting)
  );
  return links.filter(
    link =>
      linkMatchesReferenceTypes(link) || linkMatchesConditionalFormatting(link)
  );
};

const getHighlightedNodes = (
  allNodes: RelationshipsNode[],
  highlightedComponentType: LegendComponentType | null,
  highlightedConditionalFormattingId: string | null
): RelationshipsNode[] => {
  if (highlightedComponentType) {
    const typeIds = new Set(highlightedComponentType.ids);
    return allNodes.filter(node =>
      Boolean(node.typeModelId && typeIds.has(node.typeModelId))
    );
  }
  if (highlightedConditionalFormattingId) {
    return allNodes.filter(
      getMatchingFilterIdsFilter(new Set([highlightedConditionalFormattingId]))
    );
  }
  return [];
};

const getHighlightedLinks = (
  links: RelationshipsLink[],
  highlightedReferenceType: LegendReferenceType | null,
  highlightedConditionalFormattingId: string | null
): RelationshipsLink[] => {
  if (highlightedReferenceType) {
    return links.filter(
      getReferenceTypesFilter([highlightedReferenceType.globalTypeIds])
    );
  }
  if (highlightedConditionalFormattingId) {
    return links.filter(
      getMatchingFilterIdsFilter(new Set([highlightedConditionalFormattingId]))
    );
  }
  return [];
};

const EMPTY_VISUAL_STATES: VisualStatesData = {
  highlightedNodes: null,
  selectedNodes: null,
  highlightedLinks: null,
  selectedLinks: null,
};

export const deriveVisualStates = (
  legendState: InteractiveRelationshipsViewLegendState,
  rootNode: RelationshipsNode | null,
  links: RelationshipsLink[]
): VisualStatesData => {
  if (!rootNode) return EMPTY_VISUAL_STATES;

  const {
    selectedComponentTypes,
    selectedReferenceTypes,
    selectedConditionalFormattingIds,
    highlightedComponentType,
    highlightedReferenceType,
    highlightedConditionalFormattingId,
  } = legendState;

  const allNodes = [...includeAllDescendants(rootNode)];

  return {
    highlightedNodes: getHighlightedNodes(
      allNodes,
      highlightedComponentType,
      highlightedConditionalFormattingId
    ),
    selectedNodes: getSelectedNodes(
      allNodes,
      selectedComponentTypes,
      selectedConditionalFormattingIds
    ),
    highlightedLinks: getHighlightedLinks(
      links,
      highlightedReferenceType,
      highlightedConditionalFormattingId
    ),
    selectedLinks: getSelectedLinks(
      links,
      selectedReferenceTypes,
      selectedConditionalFormattingIds
    ),
  };
};
